import { styled, css } from '@vebox/etui-system';
import { useState, useRef, useLayoutEffect, createElement } from 'react';
import { _ as _objectWithoutProperties, a as _objectSpread2 } from '../_rollupPluginBabelHelpers-3b7bd988.js';
import { Button, Div } from '@vebox/etui-system-elements';
import '../Svg-0180d7b5.js';
import Icon from '../Icon/Icon.js';
import '@vebox/etui-icons';
import { icons } from '../Icon/icons.js';
import '../Icon/helper.js';
import ClickOutside from '../ClickOutside/ClickOutside.js';

const _excluded = ["theme"],
  _excluded2 = ["theme"];
const bgColor = '#F2F5F5';

// basically the same as theme.shadows[10], but with negative vertical value
const boxShadow = [`0px -6px 7px -4px rgba(0,0,0,0.12)`, `0px -11px 15px 1px rgba(0,0,0,0.08)`, `0px -4px 20px 3px rgba(0,0,0,0.06)`].join(',');
const DrawerTogglerButton = styled(Button).attrs(_ref => {
  let props = _objectWithoutProperties(_ref, _excluded);
  return _objectSpread2({
    boxShadow
  }, props);
})`
  height: 36px;
  width: 96px;
  background-color: ${bgColor};
  outline: none;
  border: none;
  z-index: auto;
  cursor: pointer;
  transform: translateY(-100%);
  margin: auto;
  text-align: center;
  position: absolute;
  right: 0;
  left: 0;

  ::after {
    content: ' ';
    height: 250%;
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    width: 200%;
    margin: 0 auto;
    background-color: ${bgColor};
    transform: translate(-25%, 100%);
  }
`;
const DrawerBodyDiv = styled(Div).attrs(_ref2 => {
  let props = _objectWithoutProperties(_ref2, _excluded2);
  return _objectSpread2({
    boxShadow,
    right: 0,
    bottom: 0,
    left: 0,
    position: props.shouldRenderInParent ? 'absolute' : 'fixed',
    height: `${props.drawerHeight}px`
  }, props);
})`
  pointer-events: auto;
  transition: all 0.2s ease-out;
  ${({
  isOpen,
  hiddenDrawerPart,
  sneakPeakPercent
}) => css`
    transform: translateY(
      ${isOpen ? hiddenDrawerPart : 100 - sneakPeakPercent}%
    );
  `}
  background-color: ${bgColor};
  z-index: auto;
`;

// Add 1 px to ensure there's no scroll in drawer when content fits exactly
const SCROLL_GUARD = 1;
function getWindowHeight() {
  return window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
}
function Drawer({
  children,
  maxOpenPercent = 50,
  sneakPeakPercent = 0,
  shouldRenderInParent = false
}) {
  const [drawerHeight, setDrawerHeight] = useState(0);
  const [openDrawerTo, setOpenDrawerTo] = useState(maxOpenPercent);
  const [isOpen, setIsOpen] = useState(false);
  const drawerRef = useRef(null);
  const drawerContentRef = useRef(null);
  const hiddenDrawerPart = 100 - openDrawerTo;
  useLayoutEffect(() => {
    var _drawerRef$current, _drawerContentRef$cur;
    const parentHeight = shouldRenderInParent ? (_drawerRef$current = drawerRef.current) === null || _drawerRef$current === void 0 ? void 0 : _drawerRef$current.parentElement.offsetHeight : getWindowHeight();
    const drawerContentHeight = (_drawerContentRef$cur = drawerContentRef.current) === null || _drawerContentRef$cur === void 0 ? void 0 : _drawerContentRef$cur.offsetHeight;
    if (parentHeight) {
      setDrawerHeight(parentHeight);
      const maxOpenPx = Math.round(parentHeight * maxOpenPercent / 100);
      if (drawerContentHeight && drawerContentHeight < maxOpenPx) {
        setOpenDrawerTo(Math.ceil(SCROLL_GUARD + drawerContentHeight * 100 / parentHeight));
      }
    }
  }, [isOpen, maxOpenPercent, shouldRenderInParent]);
  return /*#__PURE__*/createElement(Div, {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    overflow: "hidden",
    elRef: drawerRef,
    pointerEvents: "none"
  }, /*#__PURE__*/createElement(ClickOutside, {
    onClickOutside: () => setIsOpen(false)
  }, /*#__PURE__*/createElement(DrawerBodyDiv, {
    shouldRenderInParent: shouldRenderInParent,
    isOpen: isOpen,
    hiddenDrawerPart: hiddenDrawerPart,
    sneakPeakPercent: sneakPeakPercent,
    drawerHeight: drawerHeight
  }, /*#__PURE__*/createElement(DrawerTogglerButton, {
    onClick: () => setIsOpen(prev => !prev),
    testId: "drawer-toggler"
  }, /*#__PURE__*/createElement(Icon, {
    glyph: icons.DOTS_HORIZONTAL,
    size: 32
  })), /*#__PURE__*/createElement(Div, {
    overflow: "auto",
    maxHeight: `${openDrawerTo}%`,
    position: "relative",
    elRef: drawerContentRef
  }, children))));
}

export default Drawer;
export { getWindowHeight };
